<script setup>
import { computed } from 'vue'
import { DateTime } from 'luxon'
import { cn } from '../../../lib/utils'

const props = defineProps({
  // Accepts the many shapes dates arrive in: epoch millis (number or numeric
  // string), ISO string, JS Date, a Luxon DateTime, or a Java
  // LocalDate/LocalDateTime array [year, month, day, hour, minute, second, nano].
  value: { type: [Number, String, Array, Object, Date], default: null },
  // Preset name — 'date' | 'short' | 'full' | 'datetime' | 'datetime-short' |
  // 'time' | 'relative' — or any Luxon toFormat token string (e.g. 'dd/MM/yyyy').
  format: { type: String, default: 'date' },
  // Text shown when the value is missing/unparseable.
  empty: { type: String, default: '—' },
  mono: { type: Boolean, default: false },
  muted: { type: Boolean, default: true },
  class: { type: null, required: false },
})

const PRESETS = {
  date: DateTime.DATE_MED,
  short: DateTime.DATE_SHORT,
  full: DateTime.DATE_FULL,
  datetime: DateTime.DATETIME_MED,
  'datetime-short': DateTime.DATETIME_SHORT,
  time: DateTime.TIME_SIMPLE,
}

function toDateTime(v) {
  if (v === null || v === undefined || v === '') return null
  if (DateTime.isDateTime(v)) return v.isValid ? v : null
  if (v instanceof Date) {
    const d = DateTime.fromJSDate(v)
    return d.isValid ? d : null
  }
  if (Array.isArray(v)) {
    // Java LocalDate/LocalDateTime: [year, month(1-based), day, hour, minute, second, nanosecond]
    const [y, mo = 1, d = 1, h = 0, mi = 0, s = 0, ns = 0] = v
    if (y == null) return null
    const dt = DateTime.fromObject({
      year: y,
      month: mo,
      day: d,
      hour: h,
      minute: mi,
      second: s,
      millisecond: Math.floor((ns || 0) / 1e6),
    })
    return dt.isValid ? dt : null
  }
  if (typeof v === 'number') {
    const d = DateTime.fromMillis(v)
    return d.isValid ? d : null
  }
  if (typeof v === 'string') {
    const s = v.trim()
    if (/^\d+$/.test(s)) {
      const d = DateTime.fromMillis(Number(s))
      return d.isValid ? d : null
    }
    let d = DateTime.fromISO(s)
    if (d.isValid) return d
    d = DateTime.fromRFC2822(s)
    if (d.isValid) return d
    d = DateTime.fromJSDate(new Date(s))
    return d.isValid ? d : null
  }
  return null
}

const dt = computed(() => toDateTime(props.value))

const text = computed(() => {
  const d = dt.value
  if (!d) return props.empty
  if (props.format === 'relative') return d.toRelative()
  if (PRESETS[props.format]) return d.toLocaleString(PRESETS[props.format])
  return d.toFormat(props.format)
})

// Full ISO on hover for precision.
const iso = computed(() => (dt.value ? dt.value.toISO() : ''))
</script>

<template>
  <span
    data-slot="format-date"
    :title="iso"
    :class="
      cn(
        'text-sm whitespace-nowrap',
        muted ? 'text-muted-foreground' : 'text-foreground',
        mono ? 'font-mono tabular-nums' : '',
        props.class,
      )
    "
    >{{ text }}</span
  >
</template>
